import type { JSX } from 'react';
import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * BP-01 §6 "Shared `lib/`: … `offline` (Query persist to IndexedDB; offline
 * banner)".
 *
 * `role="status"` rather than `role="alert"`: losing the network is not an
 * error, the app keeps working from the persisted cache, and BP-00's
 * `hello.spec.ts` asserts that the client page shows no `alert` at all.
 */

/** Subscribe to the browser's online/offline transitions. */
export function subscribeOnline(listener: () => void): () => void {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
}

/** The browser's current opinion of connectivity. */
export function onlineSnapshot(): boolean {
  return window.navigator.onLine;
}

/** True while the browser believes it is online. */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, onlineSnapshot);
}

/** Nothing while online; a status line while offline. */
export function OfflineBanner(): JSX.Element | null {
  const online = useOnline();
  const { t } = useTranslation('common');

  if (online) {
    return null;
  }
  return (
    <p role="status" data-testid="offline-banner" className="bg-accent px-4 py-2 text-accent-foreground">
      {t('banner.offline')}
    </p>
  );
}
